import React, { useMemo } from 'react';
import { Tree, Badge, Tooltip } from 'antd';
import { StarFilled, StarOutlined, FolderOpenOutlined, FolderOutlined, FileTextOutlined } from '@ant-design/icons';
import StatusIndicators from './StatusIndicators';
import styles from './StatusSidebar.module.css';

const PipelineTree = ({
  pipelines,
  statuses,
  searchTerm,
  favorites,
  history,
  selectedStatus,
  onStatusSelect,
  toggleFavorite,
  expandedKeys = [],
  onExpandedKeysChange
}) => {

  const treeData = useMemo(() => {
    const filteredPipelines = Object.values(pipelines).filter(pipeline => {
      if (!searchTerm) return true;
      return pipeline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        statuses.some(s => 
          s.pipeline_id === pipeline.id && 
          s.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    return filteredPipelines.map(pipeline => {
      const childrenStatuses = statuses
        .filter(s => s.pipeline_id === pipeline.id)
        .filter(s => {
          if (!searchTerm) return true;
          return s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            pipeline.name.toLowerCase().includes(searchTerm.toLowerCase());
        });

      const statusCount = childrenStatuses.length;
      const activeStatusCount = childrenStatuses.filter(s => 
        favorites.includes(s.amo_status_id) || history.includes(s.amo_status_id)
      ).length;

      return {
        key: `pipeline-${pipeline.id}`,
        isLeaf: false,
        selectable: false,
        title: (
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FolderOutlined style={{ color: '#8c8c8c' }} />
              <span style={{ 
                fontSize: '13px', 
                fontWeight: 500,
                color: '#262626'
              }}>
                {pipeline.name}
              </span>
            </div>
          </div>
        ),
        children: childrenStatuses.map(status => ({
          key: `status-${status.amo_status_id}`,
          isLeaf: true,
          title: (
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                width: '100%'
              }}
              onClick={() => onStatusSelect(status)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileTextOutlined 
                  style={{ 
                    color: selectedStatus?.amo_status_id === status.amo_status_id ? '#1890ff' : '#8c8c8c',
                    fontSize: '12px'
                  }} 
                />
                <span style={{ 
                  fontSize: '12px',
                  fontWeight: selectedStatus?.amo_status_id === status.amo_status_id ? 600 : 400,
                  color: selectedStatus?.amo_status_id === status.amo_status_id ? '#1890ff' : '#262626'
                }}>
                  {status.name}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <StatusIndicators status={status} />
                <Tooltip title={favorites.includes(status.amo_status_id) ? "Убрать из избранного" : "Добавить в избранное"}>
                  <button
                    className={`${styles.actionButton} ${styles.favoriteButton} ${favorites.includes(status.amo_status_id) ? styles.active : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(status.amo_status_id);
                    }}
                  >
                    {favorites.includes(status.amo_status_id) ? 
                      <StarFilled style={{ fontSize: '12px', color: '#faad14' }} /> : 
                      <StarOutlined style={{ fontSize: '12px' }} />
                    }
                  </button>
                </Tooltip>
              </div>
            </div>
          )
        }))
      };
    });
  }, [pipelines, statuses, searchTerm, favorites, history, selectedStatus]);

  const computedExpandedKeys = useMemo(() => {
    if (searchTerm && searchTerm.trim()) {
      return Object.values(pipelines).map(p => `pipeline-${p.id}`);
    }
    return expandedKeys;
  }, [searchTerm, expandedKeys, pipelines]);

  const selectedKeys = useMemo(() => {
    const keys = [];
    if (selectedStatus) keys.push(`status-${selectedStatus.amo_status_id}`);
    return keys;
  }, [selectedStatus]);

  if (treeData.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FolderOutlined className={styles.emptyIcon} />
        <div className={styles.emptyText}>
          {searchTerm ? 'Нет результатов поиска' : 'Нет доступных воронок'}
        </div>
      </div>
    );
  }

  return (
    <Tree
      treeData={treeData}
      expandedKeys={computedExpandedKeys}
      selectedKeys={selectedKeys}
      onExpand={onExpandedKeysChange}
      showIcon={false}
      blockNode
      style={{
        background: 'transparent',
        fontSize: '13px'
      }}
    />
  );
};

export default PipelineTree; 